import { useUserNotifications } from '@hooks/queries/notifications/useUserNotifications';
import { useTranslation } from 'react-i18next';
import type { NotificationCenter } from '../../NotificationCenter.models';
import {
  DETAILS_KEY,
  FALLBACK_TITLE_KEY,
  formatThreshold,
} from './NdviThresholdNotification.utils';

/**
 * Resolves the card copy. The title is keyed by `{scenario}_{direction}` — the two together
 * decide both the sentence shape and whether it reads "dropped below" or "spiked above" —
 * and degraded names (null when Base enrichment failed) fall back to a generic label rather
 * than leaving the slot empty.
 */
function useNdviThresholdPhrases(notification: NotificationCenter.Notification) {
  const { t, i18n } = useTranslation();
  const { data } = useUserNotifications();
  const content = data?.[notification.id]?.user_notification.content;
  const namespace = `notifications.${notification.service}`;

  const details = t(`${namespace}.${content?.details || DETAILS_KEY}`, {
    defaultValue: t(`${namespace}.${DETAILS_KEY}`),
  });

  const fallbackTitle = t(`${namespace}.${content?.title || FALLBACK_TITLE_KEY}`, {
    defaultValue: t(`${namespace}.${FALLBACK_TITLE_KEY}`),
  });

  const scenario = content?.scenario ?? notification.trigger;
  const direction = content?.direction;
  const threshold = content?.threshold;

  const title =
    direction && typeof threshold === 'number'
      ? t(`${namespace}.${scenario}_${direction}`, {
          defaultValue: fallbackTitle,
          farm_name: content?.farm_name || t(`${namespace}.unnamed_farm`),
          field_name: content?.field_name || t(`${namespace}.unnamed_field`),
          org_name: content?.org_name || t(`${namespace}.unnamed_organization`),
          fields_count: content?.fields_count,
          farms_count: content?.farms_count,
          threshold: formatThreshold(threshold, i18n.language),
        })
      : fallbackTitle;

  return { content, title, details };
}

export { useNdviThresholdPhrases };
